import React, { Fragment, useCallback, useEffect, useRef } from 'react'
import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css'
import $ from 'jquery'
import 'datatables.net-bs5'

const moveItem = (items, fromIndex, toIndex) => {
    const nextItems = [...items]
    const [movedItem] = nextItems.splice(fromIndex, 1)
    nextItems.splice(toIndex, 0, movedItem)
    return nextItems
}

const computeInitialOrder = ({ ordering, defaultSort, preserveInputOrder }) => {
    if (!ordering) return []
    if (defaultSort) return [[defaultSort.columnIndex, defaultSort.order]]
    if (preserveInputOrder) return []
    return [[0, 'asc']]
}

const joinClassNames = (...classes) => classes.filter(Boolean).join(' ')

const DataTable = ({
    data,
    columns,
    onDraw,
    iscolumnfilter,
    islanguagefilter,
    iscolumnhidden,
    iscolumnfooter,
    className = '',
    defaultSort,
    ordering = true,
    searching = true,
    preserveInputOrder = false,
    isRowDraggable = false,
    getRowId,
    onRowReorder,
    pageLength = 10,
}) => {
    const tableRef = useRef()
    const dragStateRef = useRef({ sourceId: null })
    const dragHandlersRef = useRef(new WeakMap())

    const getOrderedRowIds = useCallback(
        (tableApi) => {
            const ids = tableApi
                .rows({ search: 'applied', order: 'applied' })
                .data()
                .toArray()
                .map((row) => (typeof getRowId === 'function' ? getRowId(row) : null))
                .filter(Boolean)

            return ids
        },
        [getRowId],
    )

    const attachRowDragHandlers = useCallback(
        (tableApi) => {
            if (!isRowDraggable || typeof getRowId !== 'function' || typeof onRowReorder !== 'function') {
                return
            }

            const rowNodes = tableApi.rows({ search: 'applied', order: 'applied' }).nodes().toArray()

            const handlersByNode = dragHandlersRef.current

            rowNodes.forEach((rowNode) => {
                const rowData = tableApi.row(rowNode).data()
                const rowId = getRowId(rowData)
                if (!rowId) {
                    rowNode.draggable = false
                    return
                }

                const existingHandlers = handlersByNode.get(rowNode)
                if (existingHandlers) {
                    rowNode.removeEventListener('dragstart', existingHandlers.onDragStart)
                    rowNode.removeEventListener('dragend', existingHandlers.onDragEnd)
                    rowNode.removeEventListener('dragover', existingHandlers.onDragOver)
                    rowNode.removeEventListener('dragleave', existingHandlers.onDragLeave)
                    rowNode.removeEventListener('drop', existingHandlers.onDrop)
                }

                rowNode.draggable = true
                rowNode.dataset.rowId = rowId
                rowNode.classList.add('data-table-draggable-row')

                const onDragStart = (event) => {
                    dragStateRef.current.sourceId = rowId
                    rowNode.classList.add('is-dragging')
                    event.dataTransfer.effectAllowed = 'move'
                    event.dataTransfer.setData('text/plain', rowId)
                }

                const onDragEnd = () => {
                    dragStateRef.current.sourceId = null
                    rowNode.classList.remove('is-dragging')
                    rowNodes.forEach((node) => node.classList.remove('drag-over'))
                }

                const onDragOver = (event) => {
                    event.preventDefault()
                    event.dataTransfer.dropEffect = 'move'
                    rowNode.classList.add('drag-over')
                }

                const onDragLeave = () => {
                    rowNode.classList.remove('drag-over')
                }

                const onDrop = (event) => {
                    event.preventDefault()
                    rowNode.classList.remove('drag-over')

                    const sourceId = dragStateRef.current.sourceId
                    const targetId = rowId
                    if (!sourceId || !targetId || sourceId === targetId) {
                        return
                    }

                    const currentOrder = getOrderedRowIds(tableApi)
                    const fromIndex = currentOrder.indexOf(sourceId)
                    const toIndex = currentOrder.indexOf(targetId)

                    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
                        return
                    }

                    const orderedIds = moveItem(currentOrder, fromIndex, toIndex)
                    onRowReorder({
                        sourceId,
                        targetId,
                        orderedIds,
                    })
                }

                rowNode.addEventListener('dragstart', onDragStart)
                rowNode.addEventListener('dragend', onDragEnd)
                rowNode.addEventListener('dragover', onDragOver)
                rowNode.addEventListener('dragleave', onDragLeave)
                rowNode.addEventListener('drop', onDrop)

                handlersByNode.set(rowNode, {
                    onDragStart,
                    onDragEnd,
                    onDragOver,
                    onDragLeave,
                    onDrop,
                })
            })
        },
        [getOrderedRowIds, getRowId, isRowDraggable, onRowReorder],
    )

    const footerExits = () => {
        const tableExisted = tableRef.current
        const footerExisted = tableExisted.querySelector('tfoot')

        // Check if footer already exists if exist then remove
        if (footerExisted) {
            $(footerExisted).remove()
        }
    }

    const footerCall = useCallback(() => {
        footerExits()
        const footerRow = document.createElement('tr')

        columns.forEach((column) => {
            const footerCell = document.createElement('th')
            const input = column.title
            footerCell.append(input)
            footerRow.append(footerCell)
        })

        $(tableRef.current).append($('<tfoot>').append(footerRow))
    }, [columns])

    const columnHidden = () => {
        if ($(tableRef.current).length) {
            if ($.fn.DataTable.isDataTable($(tableRef.current))) {
                // If it exists then destroy it first
                $(tableRef.current).DataTable().destroy()
            }
            const hiddentable = $(tableRef.current).DataTable({})
            $('a.toggle-vis').on('click', (event) => {
                event.preventDefault()
                const column = hiddentable.column($(this).attr('data-column'))
                column.visible(!column.visible())
            })
        }
    }

    const columnFilter = useCallback(() => {
        footerExits()

        const table = $(tableRef.current).DataTable()

        const footerRow = document.createElement('tr')
        columns.forEach((column) => {
            const footerCell = document.createElement('td')
            const input = document.createElement('input')
            input.type = 'text'
            input.className = 'form-control form-control-sm'
            input.placeholder = column.title
            input.addEventListener('keyup', (event) => {
                const columnIndex = columns.findIndex((c) => c.title === column.title)
                table.columns(columnIndex).search(event.target.value).draw()
            })
            footerCell.append(input)
            footerRow.append(footerCell)
        })

        $(tableRef.current).append($('<tfoot>').append(footerRow))
    }, [columns])

    const multiLanguage = () => {
        if ($(tableRef.current).length) {
            const languageSelect = () => {
                return Array.from(document.querySelector('#langSelector').options)
                    .filter((option) => option.selected)
                    .map((option) => option.getAttribute('data-path'))
            }
            const dataTableInit = () => {
                const langUrl = languageSelect()
                if (langUrl) {
                    $(tableRef.current).DataTable({
                        language: {
                            url: langUrl,
                        },
                    })
                }
            }
            const langSelector = document.querySelector('#langSelector')

            if (langSelector) {
                langSelector.addEventListener('change', () => {
                    $(tableRef.current).dataTable().fnDestroy()
                    dataTableInit()
                })
            }
        }
    }

    useEffect(() => {
        if (tableRef.current) {
            const table = $(tableRef.current).DataTable({
                autoWidth: false,
                data: data,
                columns: columns,
                order: computeInitialOrder({ ordering, defaultSort, preserveInputOrder }),
                ordering,
                searching,
                pageLength,
                dom:
                    (searching ? '<"row align-items-center"<"col-md-6 ps-4" f>>' : '') +
                    '<"table-responsive mt-2" rt><"row align-items-center py-2 border-top"<"col d-flex justify-content-center" p><"col-auto pe-4" l>><"clear">',
                pagingType: 'full_numbers',
                language: {
                    search: '',
                    searchPlaceholder: 'Search by asset name',
                    lengthMenu: '_MENU_ per page',
                    paginate: {
                        first: '<svg width="11" height="10" viewBox="-1 -1 12 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 0L0 4L4 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M9 0L5 4L9 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
                        previous:
                            '<svg width="6" height="10" viewBox="-1 -1 6 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 0L0 4L4 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
                        next: '<svg width="6" height="10" viewBox="-1 -1 6 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 0L4 4L0 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
                        last: '<svg width="11" height="10" viewBox="-1 -1 12 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 8L4 4L0 0" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M5 8L9 4L5 0" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
                    },
                },
                destroy: true,
                drawCallback: function () {
                    attachRowDragHandlers(this.api())
                    if (onDraw) onDraw()
                },
                initComplete: function () {
                    // Inject search icon into filter
                    const wrapper = $(this.api().table().container())
                    const filterLabel = wrapper.find('.dataTables_filter label')
                    if (filterLabel.length && !filterLabel.find('.search-icon').length) {
                        filterLabel.prepend(
                            '<span class="search-icon"><svg width="14" height="14" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">' +
                                '<circle cx="7" cy="7" r="6.25" stroke="#899CBD" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
                                '<path d="M11 12L15 16" stroke="#899CBD" stroke-width="1.5" stroke-linecap="round"/>' +
                                '</svg></span>',
                        )
                    }
                    // Footer
                    if (iscolumnfooter) {
                        footerCall()
                    }
                    // Column filter
                    if (iscolumnfilter) {
                        columnFilter()
                    }
                    // Column Hidden
                    if (iscolumnhidden) {
                        columnHidden()
                    }
                    // Multi language
                    if (islanguagefilter) {
                        multiLanguage()
                    }
                    attachRowDragHandlers(this.api())
                },
            })

            return () => {
                table.destroy()
            }
        }
    }, [
        iscolumnfilter,
        iscolumnfooter,
        iscolumnhidden,
        islanguagefilter,
        columns,
        data,
        footerCall,
        columnFilter,
        attachRowDragHandlers,
        defaultSort,
        onDraw,
        ordering,
        searching,
        pageLength,
        preserveInputOrder,
        isRowDraggable,
    ])

    return (
        <Fragment>
            <table
                className={joinClassNames('table', className, isRowDraggable && 'is-row-draggable')}
                width="100%"
                ref={tableRef}
                iscolumnfilter={iscolumnfilter}
                islanguagefilter={islanguagefilter}
            ></table>
        </Fragment>
    )
}

export default DataTable
